import React, { useEffect, useRef } from 'react';
import { translateKeystroke, getReverseKeyMap } from '../Data/KeyboardEngine';

const PracticeArea = ({ 
  typingLanguage, 
  useSplitScreen, 
  useBlockUI, 
  textToType, 
  userInput, 
  currentError, 
  textBlocks, 
  errorIndexes, 
  isDarkMode,
  setTargetKeyToHighlight, // 🚀 NEW: Callback to update virtual keyboard key
  setNeedsShift            // 🚀 NEW: Callback to update virtual keyboard shift state
}) => {
  
  const topBoxRef = useRef(null);
  const bottomBoxRef = useRef(null);
  const standardBoxRef = useRef(null);

  const t = {
    bg: isDarkMode ? '#0f172a' : '#f1f5f9',
    boxBg: isDarkMode ? '#1e293b' : 'white',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    textReady: isDarkMode ? '#94a3b8' : '#475569',
    textFaded: isDarkMode ? '#334155' : '#cbd5e1', 
    textDone: isDarkMode ? '#4ade80' : '#16a34a',
    textError: isDarkMode ? '#f87171' : '#dc2626',
    bgError: isDarkMode ? '#7f1d1d' : '#fee2e2',
    activeHighlight: isDarkMode ? '#eab308' : '#fde047',
    activeText: isDarkMode ? '#0f172a' : 'black',
    typedText: isDarkMode ? '#f8fafc' : '#0f172a',
    cursor: isDarkMode ? '#38bdf8' : '#0ea5e9' 
  };

  const customFontFamily = typingLanguage === 'kruti_dev' 
    ? "'Kruti Dev 010', sans-serif" 
    : (typingLanguage === 'inscript' || typingLanguage === 'remington_gail' || typingLanguage === 'remington_cbi') 
      ? "'Mangal', 'Arial Unicode MS', sans-serif" 
      : "'Courier New', Courier, monospace";
      
  const splitFontSize = (typingLanguage === 'hindi' || typingLanguage === 'kruti_dev' || typingLanguage === 'inscript' || typingLanguage === 'remington_gail' || typingLanguage === 'remington_cbi') ? '30px' : '24px';
  const blockFontSize = (typingLanguage === 'hindi' || typingLanguage === 'kruti_dev' || typingLanguage === 'inscript' || typingLanguage === 'remington_gail' || typingLanguage === 'remington_cbi') ? '32px' : '24px';
  const standardFontSize = (typingLanguage === 'hindi' || typingLanguage === 'kruti_dev' || typingLanguage === 'inscript' || typingLanguage === 'remington_gail' || typingLanguage === 'remington_cbi') ? '30px' : '22px';

  const safeText = typeof textToType === 'string' ? textToType : (Array.isArray(textToType) ? textToType.join(' ') : String(textToType || ""));
  const userStr = typeof userInput === 'string' ? userInput : String(userInput || "");

  // ==========================================
  // 🚀 AUTOMATED KEYBOARD HIGHLIGHT SYNC EFFECT
  // ==========================================
  useEffect(() => {
    let currentTargetChar = null;
    let physicalKey = null;
    let shiftRequired = false;

    if (safeText.length > 0 && userStr.length !== undefined) {
      if (userStr.length < safeText.length) {
        currentTargetChar = safeText[userStr.length];
      } else {
        currentTargetChar = ' '; // Fallback to space indicator when target line concludes
      }
    }

    if (currentTargetChar) {
      if (currentTargetChar === '↵') {
        physicalKey = 'enter';
      } else if (currentTargetChar === ' ') {
        physicalKey = 'space';
      } else {
        // Run reverse engine metrics conversion mapping configuration layout sequence rules
        const mapping = getReverseKeyMap(currentTargetChar, typingLanguage);
        physicalKey = mapping.physicalKey;
        shiftRequired = mapping.needsShift;
      }
    }

    // Safely bubble tracking variables up to parent state context matrix layout systems
    if (setTargetKeyToHighlight) setTargetKeyToHighlight(physicalKey);
    if (setNeedsShift) setNeedsShift(shiftRequired);
  }, [userStr, safeText, typingLanguage, setTargetKeyToHighlight, setNeedsShift]);

  useEffect(() => {
    const handleScroll = (container, activeElem, linkedContainer = null) => {
      if (!container || !activeElem) return;
      
      const topPos = activeElem.offsetTop;
      const containerScrollY = container.scrollTop;
      const containerHeight = container.clientHeight;
      let targetScrollY = containerScrollY;

      if (topPos > containerScrollY + containerHeight - 45) {
        targetScrollY = topPos - containerHeight + 45; 
      } else if (topPos < containerScrollY + 20 && containerScrollY > 0) {
        targetScrollY = Math.max(0, topPos - 20);
      }

      const leftPos = activeElem.offsetLeft;
      const containerScrollX = container.scrollLeft;
      const containerWidth = container.clientWidth;
      let targetScrollX = containerScrollX;

      if (leftPos > containerScrollX + containerWidth - 100) {
        targetScrollX = leftPos - containerWidth + 100;
      } else if (leftPos < containerScrollX + 50 && containerScrollX > 0) {
        targetScrollX = Math.max(0, leftPos - 50);
      }

      if (targetScrollY !== containerScrollY || targetScrollX !== containerScrollX) {
        container.scrollTo({ top: targetScrollY, left: targetScrollX, behavior: 'smooth' });
        if (linkedContainer) {
          linkedContainer.scrollTo({ top: targetScrollY, left: targetScrollX, behavior: 'smooth' });
        }
      }
    };

    if (useSplitScreen) {
      handleScroll(topBoxRef.current, topBoxRef.current?.querySelector('#active-target-char'), bottomBoxRef.current);
    }
  }, [userInput, useSplitScreen, useBlockUI]);

  const wordStatuses = [];
  let currentStart = 0;
  for (let i = 0; i <= safeText.length; i++) {
    if (i === safeText.length || safeText[i] === ' ' || safeText[i] === '↵') {
      let hasError = false;
      let isCurrentWord = userStr.length >= currentStart && userStr.length <= i;

      for (let j = currentStart; j < i; j++) {
        if (j < userStr.length && userStr[j] !== safeText[j]) hasError = true;
      }

      for (let j = currentStart; j <= i; j++) {
        wordStatuses[j] = { isCurrentWord, hasError };
      }
      currentStart = i + 1;
    }
  }

  const renderTopText = () => {
    return safeText.split('').map((char, index) => {
      let isCurrentChar = index === userStr.length;
      let textColor = index < userStr.length ? t.textFaded : t.textReady;
      let exactCharIndicator = isCurrentChar ? `inset 0 -3px 0 ${t.activeHighlight}` : 'none';

      return (
        <React.Fragment key={index}>
          <span id={isCurrentChar ? "active-target-char" : undefined} style={{ color: textColor, backgroundColor: 'transparent', padding: '2px 0px', boxShadow: exactCharIndicator }}>
            {char === '↵' ? '⏎' : char}
          </span>
          {char === '↵' && <br />}
        </React.Fragment>
      );
    });
  };

  if (useSplitScreen) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0, gap: '15px', overflow: 'hidden', marginTop: '10px' }}>
        <div ref={topBoxRef} style={{ position: 'relative', flex: '1 1 0', minHeight: 0, backgroundColor: t.bg, padding: '20px', borderRadius: '12px', border: `1px solid ${t.border}`, overflowY: 'auto', fontSize: splitFontSize, fontFamily: customFontFamily, lineHeight: '1.8', color: t.textReady, boxSizing: 'border-box', whiteSpace: 'pre-wrap', wordBreak: 'normal', transition: 'all 0.3s' }}>
          {renderTopText()}
        </div>

        <div ref={bottomBoxRef} style={{ position: 'relative', flex: '1 1 0', minHeight: 0, backgroundColor: t.boxBg, padding: '20px', borderRadius: '12px', border: `2px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, overflowY: 'hidden', fontSize: splitFontSize, fontFamily: customFontFamily, lineHeight: '1.8', color: t.typedText, boxSizing: 'border-box', whiteSpace: 'pre-wrap', wordBreak: 'normal', transition: 'all 0.3s' }}>
          {safeText.split('').map((char, index) => {
            const { hasError } = wordStatuses[index] || {};
            const isTyped = index < userStr.length;
            const isCorrect = isTyped && userStr[index] === safeText[index];
            let isSpace = char === ' ' || char === '↵';

            let textColor = 'transparent';
            let bgColor = 'transparent';

            if (isTyped) {
                textColor = hasError && !isSpace ? t.textError : t.typedText;
                bgColor = !isCorrect ? t.bgError : 'transparent';
            }

            let isCurrentChar = index === userStr.length;
            let cursorStyle = isCurrentChar ? `-2px 0 0 ${t.cursor}` : 'none'; 

            let displayChar = isTyped ? userStr[index] : char;
            let finalRender = displayChar;
            if (displayChar === '↵') finalRender = '⏎';
            if (!isTyped && char === '↵') finalRender = ' '; 

            return (
              <React.Fragment key={index}>
                <span id={isCurrentChar ? "active-typed-cursor" : undefined} style={{ color: textColor, backgroundColor: bgColor, borderRadius: '4px', boxShadow: cursorStyle, paddingLeft: isCurrentChar && !isSpace ? '2px' : '0px' }}>
                  {finalRender}
                </span>
                {char === '↵' && <br />}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }

  if (useBlockUI) {
    return (
      <div style={{ flex: 1, width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', alignContent: 'center', rowGap: '20px', padding: '10px 20px', boxSizing: 'border-box' }}>
        {(textBlocks || []).map((wordGroup, wIdx) => {
          if (!wordGroup || wordGroup.length === 0) return null;
          const lastCharIndex = wordGroup[wordGroup.length - 1].index;
          const isWordFinished = userStr.length > lastCharIndex;
          let hasWordError = false;
          wordGroup.forEach(charObj => { if ((errorIndexes || []).includes(charObj.index)) hasWordError = true; });

          return (
            <div key={wIdx} style={{ display: 'flex', alignItems: 'center', gap: '6px', justifySelf: wIdx % 2 === 0 ? 'start' : 'end' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                {wordGroup.map(({char, index}) => {
                  let textColor = isDarkMode ? '#94a3b8' : '#64748b'; 
                  let bgColor = isDarkMode ? '#1e293b' : '#f8fafc'; 
                  let borderColor = isDarkMode ? '#334155' : '#e2e8f0';
                  let isCurrent = index === userStr.length;
                  
                  if (index < userStr.length) {
                    if (userStr[index] === char) { textColor = isDarkMode ? '#4ade80' : '#166534'; bgColor = isDarkMode ? '#064e3b' : '#dcfce7'; borderColor = isDarkMode ? '#10b981' : '#22c55e'; }
                    else { textColor = isDarkMode ? '#fca5a5' : '#991b1b'; bgColor = isDarkMode ? '#7f1d1d' : '#fee2e2'; borderColor = isDarkMode ? '#ef4444' : '#ef4444'; }
                  } else if (isCurrent) { 
                    bgColor = currentError ? (isDarkMode ? '#7f1d1d' : '#fee2e2') : t.activeHighlight; 
                    textColor = currentError ? (isDarkMode ? '#fca5a5' : '#991b1b') : t.activeText; 
                    borderColor = currentError ? '#ef4444' : '#eab308'; 
                  }

                  if (char === ' ') {
                    return <div key={index} id={isCurrent ? "active-target-char" : undefined} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '48px', height: '45px', backgroundColor: bgColor, color: textColor, border: `2px solid ${borderColor}`, borderRadius: '8px', fontSize: '10px', fontWeight: 'bold', boxShadow: isCurrent ? '0 4px 10px rgba(234, 179, 8, 0.3)' : 'none', transition: 'all 0.1s' }}>SPACE</div>;
                  } else {
                    return <div key={index} id={isCurrent ? "active-target-char" : undefined} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '38px', height: '45px', backgroundColor: bgColor, color: textColor, border: `2px solid ${borderColor}`, borderRadius: '8px', fontSize: blockFontSize, fontFamily: customFontFamily, fontWeight: 'bold', boxShadow: isCurrent ? '0 4px 10px rgba(234, 179, 8, 0.3)' : 'none', transition: 'all 0.1s' }}>{char}</div>;
                  }
                })}
              </div>
              <div style={{ width: '24px', fontSize: '20px', marginLeft: '4px', textAlign: 'center' }}>{isWordFinished ? (hasWordError ? '❌' : '✔️') : ''}</div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ width: '100%', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
      <div ref={standardBoxRef} style={{ position: 'relative', width: '100%', height: '80px', overflowX: 'hidden', overflowY: 'hidden', backgroundColor: t.bg, fontSize: standardFontSize, fontFamily: customFontFamily, borderRadius: '12px', border: `2px solid ${t.border}`, textAlign: 'center', lineHeight: '76px', boxSizing: 'border-box', padding: '0 50px', boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.02)', whiteSpace: 'nowrap' }}>
        {safeText.split('').map((char, index) => {
          let textColor = t.textReady; 
          let bgColor = 'transparent';
          let isCurrent = index === userStr.length;
          let isSpace = char === ' ' || char === '↵';
          
          if (index < userStr.length) {
            textColor = 'transparent';
            bgColor = 'transparent';
          } else if (isCurrent) {
            if (currentError) {
              textColor = t.textError; 
              bgColor = t.bgError; 
            } else {
              bgColor = isSpace ? 'transparent' : t.activeHighlight; 
              textColor = t.activeText; 
            }
          }

          let exactCharIndicator = (isCurrent && isSpace && !currentError) ? `inset 0 -4px 0 ${t.activeHighlight}` : 'none';
          if (isCurrent && isSpace && currentError) exactCharIndicator = `inset 0 -4px 0 ${t.textError}`;
          let finalRender = char === '↵' ? '⏎' : char;

          return (
            <React.Fragment key={index}>
              <span id={isCurrent ? "active-target-char" : undefined} style={{ color: textColor, backgroundColor: bgColor, borderRadius: '6px', padding: '2px 4px', margin: '0 1px', boxShadow: exactCharIndicator }}>
                {finalRender}
              </span>
              {char === '↵' && <br />}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default PracticeArea;